import { useEffect, useState } from "react";
import type { Conversation } from "@swift/shared";
import { useStore } from "../lib/store";
import { api } from "../lib/api";
import { getMutedConversationIds, setConversationMuted } from "../lib/settings";
import FileBrowser from "./FileBrowser";

/**
 * Dunne titelbalk boven de berichtenstroom: toont de naam van het kanaal
 * of de DM-partner, plus een belletje om het gesprek te dempen.
 *
 * Mute werkt op twee niveaus:
 *  - **Server** (Conversation.muted): bepaalt of Web Push wordt verstuurd
 *    naar dit account.
 *  - **Lokaal** (localStorage): bepaalt of dit specifieke apparaat
 *    geluid/toast/flash geeft voor real-time berichten.
 *
 * De belknop toggelt beide tegelijk — vanuit gebruikersperspectief is
 * "dempen" één actie. De server-state is leidend (komt mee uit de
 * conversation-list); de lokale state is per-apparaat-fallback.
 */
export default function ConversationHeader({ conversation }: { conversation: Conversation }) {
  const me = useStore((s) => s.me);
  const users = useStore((s) => s.users);
  const upsertConversation = useStore((s) => s.upsertConversation);
  const [muted, setMuted] = useState(
    () => conversation.muted || getMutedConversationIds().has(conversation.id),
  );
  const [busy, setBusy] = useState(false);
  const [fileBrowserOpen, setFileBrowserOpen] = useState(false);

  // Houd lokaal-mute en server-mute in sync wanneer de conversatie
  // (her)laadt of via socket-event verandert.
  useEffect(() => {
    setMuted(conversation.muted || getMutedConversationIds().has(conversation.id));
  }, [conversation.id, conversation.muted]);

  async function toggleMute() {
    const next = !muted;
    setMuted(next);
    setConversationMuted(conversation.id, next); // lokaal direct
    setBusy(true);
    try {
      await api.setConversationMuted(conversation.id, next);
      // Update de in-memory conversation zodat de Sidebar etc. weten dat
      // 't gemute is (handig voor toekomstige badge-styling).
      upsertConversation({ ...conversation, muted: next });
    } catch {
      // Revert bij fout — server is leidend voor push, lokaal voor UX.
      setMuted(!next);
      setConversationMuted(conversation.id, !next);
    } finally {
      setBusy(false);
    }
  }

  let title: string;
  if (conversation.kind === "channel") {
    title = `# ${conversation.name ?? "kanaal"}`;
  } else {
    const otherId = conversation.member_ids.find((id) => id !== me?.id);
    const other = otherId !== undefined ? users[otherId] : undefined;
    title = other?.display_name ?? "Gesprek";
  }

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-slate-700 bg-slate-800 px-4 py-2">
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-200">{title}</span>
      <button
        onClick={() => setFileBrowserOpen(true)}
        title="Bestanden in dit gesprek"
        aria-label="Bestanden in dit gesprek"
        className="flex h-7 w-7 items-center justify-center rounded text-slate-300 hover:bg-slate-700"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      </button>
      <button
        onClick={() => void toggleMute()}
        disabled={busy}
        title={muted ? "Dempen opheffen" : "Gesprek dempen (geen meldingen)"}
        aria-label={muted ? "Dempen opheffen" : "Gesprek dempen"}
        className={`flex h-7 w-7 items-center justify-center rounded disabled:opacity-50 ${
          muted ? "text-slate-500 hover:bg-slate-700" : "text-slate-300 hover:bg-slate-700"
        }`}
      >
        {muted ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
            <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
            <path d="M18 8a6 6 0 0 0-9.33-5" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        )}
      </button>
      {fileBrowserOpen && (
        <FileBrowser conversation={conversation} onClose={() => setFileBrowserOpen(false)} />
      )}
    </div>
  );
}
